/**
 * API 取得データの保存と、保存直後に必要な rule 適用までを行う。
 */
import type { Action } from '@0xtorch/core'
import { divideArrayIntoChunks } from '@pkg/basic'
import { and, eq, inArray, ne } from 'drizzle-orm'
import {
  actionAccountRelationColumnCount,
  actionColumnCount,
  actionSourceColumnCount,
  actionTransferColumnCount,
  cryptoCurrencyColumnCount,
  nftColumnCount,
  singleActionRuleColumnCount,
  sqliteMaxCompoundSelectCount,
  sqliteMaxHostParameterCount,
} from '../../constants'
import { actionsToInsertSqls } from '../../createSql/actionsToInsertSqls'
import {
  accountApiPendingTable,
  actionAccountRelationTable,
  actionSourceTable,
  actionTable,
  actionTransferTable,
  cryptoCurrencyTable,
  nftTable,
  portfolioTable,
  singleActionRuleTable,
} from '../../schema'
import type { DatabaseWithTransaction } from '../../types'
import { getMaxInsertRowCount } from '../../utils'
import { applyActionEditRulesToInsertedActions } from './applyActionEditRulesToInsertedActions'
import { deleteAssistantProposalsForSourceIds } from './deleteAssistantProposalsForActionKeys'

type SaveApiDataParameters = {
  accountId: number
  actions: Action[]
  apiId: number
  database: DatabaseWithTransaction
  pendingChange: {
    id: number
    action: 'update' | 'delete'
    from: number
    to: number
  }
  sources: string[]
}

type ActionAccountRelationRow = typeof actionAccountRelationTable.$inferInsert

type SingleActionRuleRow = typeof singleActionRuleTable.$inferInsert

/**
 * API source ごとに自動で作成する single action rule の定義。
 */
const singleActionRuleDefinitions = [
  {
    targetType: 'gift',
    newType: 'receive-from-cex',
  },
  {
    targetType: 'income',
    newType: 'receive-from-cex',
  },
  {
    targetType: 'fee',
    newType: 'send-to-cex',
  },
  {
    targetType: 'transfer',
    newType: 'send-to-cex',
  },
] as const

/**
 * API データを保存して、 API Pending を更新する
 * - 既存の同一 action source に関連する action 削除
 * - action 関連データ保存
 *   - crypto currency 保存
 *   - nft 保存
 *   - action source 保存 (on conflict do nothing)
 *   - action 保存 (apiId を設定)
 *   - action transfer 保存
 *   - action account relation 保存
 * - sources から single action rule 作成して保存 (on conflict do nothing)
 * - single action rule に該当する action を更新
 * - portfolio の　isTxGenerated を false に変更
 * - pendingChange の内容に応じて、API Pending を更新
 *   - update: from, to を更新
 *   - delete: 対象の AccountApiPendingTable data 削除
 * @param params 保存対象の action 群と関連情報を受け取る。
 * @returns 値は返さない。
 */
export const saveApiData = async ({
  accountId,
  actions,
  apiId,
  database: { database, transaction },
  pendingChange,
  sources,
}: SaveApiDataParameters): Promise<void> => {
  const insertedActionIdList = createActionIdList({ actions })
  const actionSources = createActionSources({ actions })

  const { actionSqls, actionTransferSqls, cryptoSqls, nftSqls } =
    actionsToInsertSqls(
      actions.map((action) => ({
        ...action,
        apiId,
      })),
    )

  const actionAccountRelations = createActionAccountRelations({
    accountId,
    actions,
  })
  const rules = createSingleActionRules({ sources })

  await transaction([
    (tx) => [
      deleteAssistantProposalsForSourceIds({
        database: tx,
        sourceIds: actionSources,
      }),
    ],
    (tx) => [
      // 既存の同一 action source に関連する action 削除
      ...divideArrayIntoChunks(actionSources, sqliteMaxHostParameterCount).map(
        (chunk) =>
          tx
            .delete(actionTable)
            .where(inArray(actionTable.sourceId, [...chunk])),
      ),
    ],
    (tx) => {
      const pendingChangeQueries = []

      // pendingChange の内容に応じて、API Pending を更新
      if (pendingChange.action === 'update') {
        pendingChangeQueries.push(
          tx
            .update(accountApiPendingTable)
            .set({
              from: new Date(pendingChange.from),
              to: new Date(pendingChange.to),
            })
            .where(eq(accountApiPendingTable.id, pendingChange.id)),
        )
      } else {
        pendingChangeQueries.push(
          tx
            .delete(accountApiPendingTable)
            .where(eq(accountApiPendingTable.id, pendingChange.id)),
        )
      }

      return [
        // crypto currency を保存 (on conflict do nothing)
        ...divideArrayIntoChunks(
          cryptoSqls,
          getMaxInsertRowCount(cryptoCurrencyColumnCount),
        ).map((chunk) =>
          tx
            .insert(cryptoCurrencyTable)
            .values([...chunk])
            .onConflictDoNothing(),
        ),
        // nft を保存 (on conflict do nothing)
        ...divideArrayIntoChunks(
          nftSqls,
          getMaxInsertRowCount(nftColumnCount),
        ).map((chunk) =>
          tx
            .insert(nftTable)
            .values([...chunk])
            .onConflictDoNothing(),
        ),
        // action source を保存 (on conflict do nothing)
        ...divideArrayIntoChunks(
          actionSources,
          getMaxInsertRowCount(actionSourceColumnCount),
        ).map((chunk) =>
          tx
            .insert(actionSourceTable)
            .values(chunk.map((id) => ({ id, type: 'other' as const })))
            .onConflictDoNothing(),
        ),
        // action を保存 (apiId を設定)
        ...divideArrayIntoChunks(
          actionSqls,
          // SQLITE_MAX_COMPOUND_SELECT を意識して数を調整
          Math.min(
            getMaxInsertRowCount(actionColumnCount + 3),
            Math.floor(sqliteMaxCompoundSelectCount / 3),
          ),
        ).map((chunk) => tx.insert(actionTable).values([...chunk])),
        // action transfer を保存
        ...divideArrayIntoChunks(
          actionTransferSqls,
          getMaxInsertRowCount(actionTransferColumnCount),
        ).map((chunk) => tx.insert(actionTransferTable).values([...chunk])),
        // action account relation を保存 (on conflict do nothing)
        ...divideArrayIntoChunks(
          actionAccountRelations,
          getMaxInsertRowCount(actionAccountRelationColumnCount),
        ).map((chunk) =>
          tx
            .insert(actionAccountRelationTable)
            .values([...chunk])
            .onConflictDoNothing(),
        ),
        // single action rule を保存 (on conflict do nothing)
        ...divideArrayIntoChunks(
          rules,
          getMaxInsertRowCount(singleActionRuleColumnCount),
        ).map((chunk) =>
          tx
            .insert(singleActionRuleTable)
            .values([...chunk])
            .onConflictDoNothing(),
        ),
        // single action rule に該当する action を更新
        ...rules.map((rule) =>
          tx
            .update(actionTable)
            .set({
              type: rule.newType,
              evidence: 'user-rule',
              generatedTx: false,
            })
            .where(
              and(
                eq(actionTable.sourceId, rule.source),
                eq(actionTable.type, rule.targetType),
                ne(actionTable.locked, true),
              ),
            ),
        ),
        // portfolio の　isTxGenerated を false に変更
        tx
          .update(portfolioTable)
          .set({ isTxGenerated: false }),
        ...pendingChangeQueries,
      ]
    },
  ])

  await applyActionEditRulesToInsertedActions({
    database: { database, transaction },
    actionIdList: insertedActionIdList,
  })
}

/**
 * action 一覧から action id 一覧を構築する。
 * @param params action 一覧を受け取る。
 * @returns action id 一覧を返す。
 */
const createActionIdList = ({
  actions,
}: {
  readonly actions: readonly Action[]
}): readonly string[] => {
  return actions.map((action) => `${action.source}_${action.order}`)
}

/**
 * action 一覧から重複しない source 一覧を構築する。
 * @param params action 一覧を受け取る。
 * @returns 重複を除いた source 一覧を返す。
 */
const createActionSources = ({
  actions,
}: {
  readonly actions: readonly Action[]
}): readonly string[] => {
  const actionSourceSet = new Set<string>()

  for (const action of actions) {
    actionSourceSet.add(action.source)
  }

  return [...actionSourceSet]
}

/**
 * action と account の関連レコードを構築する。
 * @param params accountId と action 一覧を受け取る。
 * @returns actionAccountRelation テーブルへ保存するレコード一覧を返す。
 */
const createActionAccountRelations = ({
  accountId,
  actions,
}: {
  readonly accountId: number
  readonly actions: readonly Action[]
}): readonly ActionAccountRelationRow[] => {
  const actionAccountRelations: ActionAccountRelationRow[] = []

  for (const action of actions) {
    actionAccountRelations.push({
      actionId: `${action.source}_${action.order}`,
      accountId,
    })
  }

  return actionAccountRelations
}

/**
 * API source 一覧から自動生成する single action rule 一覧を構築する。
 * @param params source 一覧を受け取る。
 * @returns singleActionRule テーブルへ保存するレコード一覧を返す。
 */
const createSingleActionRules = ({
  sources,
}: {
  readonly sources: readonly string[]
}): readonly SingleActionRuleRow[] => {
  const rules: SingleActionRuleRow[] = []

  for (const source of sources) {
    for (const ruleDefinition of singleActionRuleDefinitions) {
      rules.push({
        source,
        targetType: ruleDefinition.targetType,
        newType: ruleDefinition.newType,
      })
    }
  }

  return rules
}
